import React from "react";
import "../../styles/institutional-pages.css";
import { motion, useReducedMotion } from "framer-motion";
import {
  BookOpen,
  Award,
  Users,
  Cloud,
  Network,
  Shield,
  MapPin,
  Phone,
  Mail,
  FileText,
  Download,
  Layers,
  File,
  ImageIcon,
  GraduationCap,
  Users2,
  BriefcaseBusiness,
  Trophy,
  Star,
  Medal,
  Newspaper,
  ShieldCheck,
  Landmark,
  Navigation,
  ExternalLink,
  Route,
} from "lucide-react";

const fallbackText = "Information will be updated by the department office.";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function safeImage(src, fallback = "/fpb.png") {
  if (!src) return fallback;
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `/${src.replace(/^\/+/, "")}`;
}

function toParagraphs(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function textFromItems(items = [], fallback = fallbackText) {
  const parts = (items || [])
    .map((item) => item.description || item.title || item.value)
    .filter(Boolean);
  return parts.length ? parts.join("\n\n") : fallback;
}

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.75, delay }}
    viewport={{ once: true }}
    className="origin-left h-1 bg-gradient-to-r from-blue-700 to-blue-400 rounded-full my-8"
  />
);

function AnimatedSection({ id, children, className = "bg-white" }) {
  return (
    <motion.section
      id={id}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      className={`${className} rounded-2xl shadow-lg p-6 md:p-8`}
    >
      {children}
    </motion.section>
  );
}

function SectionTitle({ icon: Icon, children, light = false }) {
  return (
    <h2 className="text-xl md:text-2xl font-bold flex items-center gap-3">
      {Icon && <Icon className={light ? "text-yellow-300" : "text-blue-700"} />}
      {children}
    </h2>
  );
}

function ListGrid({ items, icon: Icon = FileText }) {
  return (
    <ul className="mt-6 grid md:grid-cols-2 gap-3 text-gray-100">
      {(items || []).map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <Icon className="mt-1 shrink-0" size={20} />
          <span>{item.description || item.title || item.value}</span>
        </li>
      ))}
    </ul>
  );
}


const hodBadgeThemes = [
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-yellow-100 text-yellow-800 border-yellow-200",
  "bg-green-100 text-green-800 border-green-200",
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-red-100 text-red-800 border-red-200",
  "bg-indigo-100 text-indigo-800 border-indigo-200",
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-sky-100 text-sky-800 border-sky-200",
];

const hodIconMap = [Medal, Newspaper, GraduationCap, Star, Trophy, ShieldCheck, Award, Landmark];

function getProgressIcon(progress, index) {
  const rawIcon = String(progress.icon || progress.meta || "").trim();
  if (rawIcon && rawIcon.length <= 4) return rawIcon;
  const Icon = hodIconMap[index % hodIconMap.length];
  return <Icon size={18} />;
}

function HODProgressBadges({ items = [], reduceMotion }) {
  const list = (items || []).filter((item) => item && (item.title || item.value || item.description));
  if (!list.length) return null;

  return (
    <div className="department-hod-progress flex flex-wrap gap-3 mb-6" aria-label="HOD progress and achievements">
      {list.map((item, index) => {
        const label = item.value ? `${item.value} ${item.title || ""}`.trim() : (item.title || item.description);
        return (
          <motion.span
            key={`${label}-${index}`}
            whileHover={reduceMotion ? undefined : { y: -3, scale: 1.03 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold shadow-sm ${hodBadgeThemes[index % hodBadgeThemes.length]}`}
            title={item.description || label}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/65 shadow-sm">{getProgressIcon(item, index)}</span>
            <span>{label}</span>
          </motion.span>
        );
      })}
    </div>
  );
}

function extractIframeSrc(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = text.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : text;
}

function mapEmbedUrl(mapUrl, address) {
  const raw = extractIframeSrc(mapUrl);
  if (raw && raw.includes("google.com/maps")) {
    if (raw.includes("/embed") || raw.includes("output=embed")) return raw;
    return `${raw}${raw.includes("?") ? "&" : "?"}output=embed`;
  }
  if (raw && raw.startsWith("http")) return raw;
  const query = address || "Federal Polytechnic Bida, Niger State, Nigeria";
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function DepartmentMapSection({ contact = {}, mapPoints = [], reduceMotion }) {
  const mainPoint = mapPoints.find((item) => item.mapUrl || item.address) || null;
  const address = mainPoint?.address || contact.address || "Federal Polytechnic Bida, Niger State, Nigeria";
  const embedSrc = mapEmbedUrl(mainPoint?.mapUrl || contact.mapUrl, address);
  const title = mainPoint?.title || "Department Location";

  return (
    <AnimatedSection id="map" className="bg-white overflow-hidden">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <SectionTitle icon={MapPin}>Department Map & Location</SectionTitle>
          <p className="mt-2 max-w-3xl text-gray-600">Help students, visitors and applicants locate the department office, laboratories and key contact points.</p>
        </div>
        {(mainPoint?.mapUrl || contact.mapUrl) && (
          <a href={extractIframeSrc(mainPoint?.mapUrl || contact.mapUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white shadow hover:bg-blue-600">
            <ExternalLink size={18} /> Open Map
          </a>
        )}
      </div>
      <Divider />
      <div className="department-map-grid grid gap-6 lg:grid-cols-[1fr_360px]">
        <motion.div whileHover={reduceMotion ? undefined : { scale: 1.01 }} className="department-map-frame min-h-[320px] overflow-hidden rounded-2xl border border-blue-100 bg-blue-50 shadow-inner">
          <iframe title={title} src={embedSrc} width="100%" height="100%" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-[320px] w-full border-0 md:h-[420px]"></iframe>
        </motion.div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-blue-700 p-3 text-white"><Navigation size={22} /></div>
              <div>
                <h3 className="font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{address}</p>
              </div>
            </div>
          </div>
          {(mapPoints.length ? mapPoints : [{ title: "Department Office", address }]).map((point, index) => (
            <motion.div key={index} whileHover={reduceMotion ? undefined : { x: 4 }} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-blue-50 p-2 text-blue-700"><Route size={18} /></div>
                <div>
                  <h4 className="font-bold text-slate-900">{point.title || `Location ${index + 1}`}</h4>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{point.address || point.note || address}</p>
                  {point.note && <p className="mt-1 text-xs font-bold uppercase tracking-wide text-blue-700">{point.note}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

export default function DepartmentClassicPage({ data = {} }) {
  const reduceMotion = useReducedMotion();
  const title = data.title || data.shortName || "Department";
  const heroImage = safeImage(data.heroImage, "/images/nccb.jpg");
  const overview = toParagraphs(data.overview);
  const leader = data.leader || null;
  const contact = data.contact || {};
  const features = data.features || [];
  const offerings = data.offerings || [];
  const downloads = data.downloads || [];
  const gallery = data.gallery || [];
  const staff = (data.staff && data.staff.length ? data.staff : leader ? [{ name: leader.name, role: leader.title, bio: leader.bio, img: leader.photo }] : []) || [];
  const courseYear1 = data.courseYear1 || [];
  const courseYear2 = data.courseYear2 || [];
  const careerNetworking = data.careerNetworking || [];
  const careerCloud = data.careerCloud || [];
  const certifications = data.certifications || [];
  const brochures = data.brochures && data.brochures.length ? data.brochures : downloads;
  const admissionRequirements = data.admissionRequirements || [];
  const hodProgress = leader?.progress?.length
    ? leader.progress
    : (leader?.achievements || []).map((item) => (typeof item === "string" ? { title: item } : item));
  const mapPoints = data.mapPoints || [];
  const subtitle = data.subtitle || "Fostering innovation, practical skills and professional excellence.";

  const navItems = [
    ["brief", "Overview"],
    ["why", "Why Study"],
    ["admission", "Admission"],
    ["prospects", "Careers"],
    ["certifications", "Certifications"],
    ["staff", "Meet Staff"],
    ["courses", "Course Structure"],
    ["brochure", "Brochure"],
    ["gallery", "Gallery"],
    ["map", "Map"],
    ["contact", "Contact"],
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900 department-classic-page">
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md shadow-md py-3 px-4 md:px-20 flex gap-6 items-center overflow-x-auto whitespace-nowrap">
        <div className="flex-1 min-w-[240px] font-semibold text-blue-700">
          {data.shortName || title} — Federal Polytechnic Bida
        </div>
        <div className="flex gap-4 text-sm font-medium">
          {navItems.map(([href, label]) => (
            <a key={href} href={`#${href}`} className="hover:text-blue-500">{label}</a>
          ))}
        </div>
      </nav>

      <header className="relative h-[340px] md:h-[420px] flex items-center justify-center overflow-hidden">
        <motion.img
          src={heroImage}
          alt={`${title} banner`}
          className="absolute inset-0 w-full h-full object-cover scale-105"
          initial={reduceMotion ? false : { scale: 1.1 }}
          animate={reduceMotion ? undefined : { scale: 1.04 }}
          transition={{ duration: 3.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black/35" />
        <motion.div
          className="absolute -left-16 top-16 h-40 w-40 rounded-full bg-blue-500/20 blur-3xl"
          animate={reduceMotion ? undefined : { y: [0, 18, 0], x: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -right-20 bottom-12 h-48 w-48 rounded-full bg-cyan-300/20 blur-3xl"
          animate={reduceMotion ? undefined : { y: [0, -16, 0], x: [0, -12, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative z-10 text-center text-white px-6">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-5xl font-extrabold leading-tight"
          >
            {title}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-3 max-w-3xl mx-auto text-white/95">
            {subtitle}
          </motion.p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a href={data.primaryAction?.href || "/admissions"} className="inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-3 rounded-lg shadow hover:bg-blue-600 font-bold">
              {data.primaryAction?.label || "Apply Now"}
            </a>
            <a href={data.secondaryAction?.href || "#brochure"} className="inline-flex items-center gap-2 bg-white text-blue-700 px-5 py-3 rounded-lg shadow font-bold">
              <Download size={18} /> {data.secondaryAction?.label || "Brochure (PDF)"}
            </a>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-20 py-12 space-y-10 md:space-y-12">
        <AnimatedSection id="brief">
          <SectionTitle icon={BookOpen}>The Department in Brief</SectionTitle>
          <Divider />
          {(overview.length ? overview : [fallbackText]).map((paragraph, index) => (
            <p key={index} className="text-gray-700 text-justify leading-relaxed mt-4 first:mt-0">{paragraph}</p>
          ))}
          {(data.vision || data.mission) && (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {data.vision && <div className="rounded-xl bg-blue-50 p-4"><h3 className="font-bold text-blue-800">Vision</h3><p className="mt-2 text-gray-700 leading-relaxed">{data.vision}</p></div>}
              {data.mission && <div className="rounded-xl bg-blue-50 p-4"><h3 className="font-bold text-blue-800">Mission</h3><p className="mt-2 text-gray-700 leading-relaxed">{data.mission}</p></div>}
            </div>
          )}
        </AnimatedSection>

        {leader && (
          <AnimatedSection id="hodspeech" className="relative bg-white overflow-hidden flex flex-col md:flex-row gap-10 items-start">
            <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white pointer-events-none"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.08),transparent_70%)] pointer-events-none"></div>
            <motion.div animate={reduceMotion ? undefined : { opacity: [0.3, 0.6, 0.3], y: [0, 10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="absolute top-10 right-10 w-10 h-10 bg-blue-400/40 rounded-full blur-xl" />
            <motion.div animate={reduceMotion ? undefined : { opacity: [0.25, 0.5, 0.25], y: [0, -12, 0] }} transition={{ duration: 3, repeat: Infinity, delay: 1 }} className="absolute bottom-10 left-10 w-16 h-16 bg-blue-300/40 rounded-full blur-xl" />
            <div className="w-full md:w-1/3 flex justify-center relative z-10">
              <motion.img
                src={safeImage(leader.photo, "/fpb.png")}
                alt={leader.name || "Head of Department"}
                className="w-44 h-44 md:w-52 md:h-52 rounded-full object-cover shadow-xl border-4 border-white"
                whileHover={reduceMotion ? undefined : { rotate: 3, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              />
            </div>
            <div className="flex-1 relative z-10">
              <SectionTitle icon={Users}>{leader.sectionTitle || "HOD Speech"}</SectionTitle>
              <Divider />
              <HODProgressBadges items={hodProgress} reduceMotion={reduceMotion} />
              {leader.quote && (
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-md shadow-sm mb-6">
                  <p className="italic text-blue-900 font-medium">“{leader.quote}”</p>
                </div>
              )}
              {(toParagraphs(leader.bio).length ? toParagraphs(leader.bio) : [fallbackText]).map((paragraph, index) => (
                <p key={index} className="text-gray-700 text-justify leading-relaxed mt-4 first:mt-0">{paragraph}</p>
              ))}
              <p className="text-blue-700 font-semibold mt-6">— {leader.name}</p>
              <p className="text-gray-600">{leader.title || "Head of Department"}</p>
            </div>
          </AnimatedSection>
        )}

        <AnimatedSection id="why" className="bg-blue-900 text-white">
          <SectionTitle icon={Award} light>Why Study Here</SectionTitle>
          <Divider />
          <p className="text-gray-100 text-justify leading-relaxed">
            {features[0]?.description || "The department is designed to provide purposeful, practical and career-focused learning for students through modern academic delivery, hands-on training and industry-oriented exposure."}
          </p>
          <ListGrid items={features.length > 1 ? features.slice(1) : offerings.slice(0, 6)} icon={ImageIcon} />
        </AnimatedSection>

        <AnimatedSection id="admission">
          <SectionTitle icon={Users}>Admission Requirements</SectionTitle>
          <Divider />
          <p className="text-gray-700 text-justify leading-relaxed">{textFromItems(admissionRequirements, "Candidates are required to meet the Polytechnic and departmental entry requirements. Specific requirements can be updated by the department from the admin dashboard.")}</p>
        </AnimatedSection>

        <Divider delay={0.1} />

        <AnimatedSection id="prospects">
          <SectionTitle icon={Network}>Job Prospects</SectionTitle>
          <Divider />
          <p className="text-gray-700 text-justify leading-relaxed">Graduates of this department can serve in public and private organizations, technology-driven industries, education, enterprise development and specialist technical roles.</p>
          <div className="mt-6 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">Job Descriptions / Career Paths</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {(careerNetworking.length ? careerNetworking : offerings.slice(0, 7)).map((item, index) => <li key={index}>{item.title || item.description}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">Specialist Opportunities</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {(careerCloud.length ? careerCloud : features.slice(0, 7)).map((item, index) => <li key={index}>{item.title || item.description}</li>)}
              </ul>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection id="certifications" className="bg-blue-50">
          <SectionTitle icon={Shield}>Certification / Professional Courses</SectionTitle>
          <Divider />
          <p className="text-gray-800 text-justify leading-relaxed">Professional certifications and short courses improve employability, technical competence and industry readiness.</p>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {(certifications.length ? certifications : offerings.slice(0, 4)).map((item, index) => (
              <div key={index}>
                <h4 className="font-semibold text-blue-700 mb-2">{item.title}</h4>
                <p className="text-gray-700 leading-relaxed">{item.description || item.meta || "Certification details can be added from the admin dashboard."}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection id="staff">
          <SectionTitle icon={Users2}>Meet Our Staff</SectionTitle>
          <Divider />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(staff.length ? staff : [{ name: "Department Staff", role: "Academic Staff", bio: "Staff details will be updated soon.", img: "/fpb.png" }]).map((s, i) => (
              <motion.article whileHover={reduceMotion ? undefined : { y: -6 }} key={i} className="bg-gray-50 rounded-xl p-4 text-center shadow-sm">
                <img src={safeImage(s.img || s.image_url || s.photo, "/fpb.png")} alt={s.name} className="mx-auto w-28 h-28 rounded-full object-cover shadow-md" />
                <h3 className="mt-3 font-semibold text-gray-800">{s.name || s.title}</h3>
                <p className="text-sm text-blue-700">{s.role || s.meta}</p>
                <p className="mt-2 text-sm text-gray-600">{s.bio || s.description}</p>
              </motion.article>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection id="courses">
          <SectionTitle icon={GraduationCap}>Course Structure</SectionTitle>
          <Divider />
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3">Year 1 / Core Modules</h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-1">
                {(courseYear1.length ? courseYear1 : offerings.slice(0, 7)).map((m, i) => <li key={i}>{m.title || m.description}</li>)}
              </ol>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3">Year 2 / Advanced Modules</h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-1">
                {(courseYear2.length ? courseYear2 : offerings.slice(7, 14)).map((m, i) => <li key={i}>{m.title || m.description}</li>)}
                {!courseYear2.length && offerings.length <= 7 && <li>Advanced departmental modules will be updated soon.</li>}
              </ol>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection id="brochure" className="bg-blue-900 text-white">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <SectionTitle icon={File} light>Admission Brochure</SectionTitle>
            {brochures[0]?.href && <a href={brochures[0].href} download className="inline-flex items-center gap-2 bg-white text-blue-900 px-4 py-2 rounded-md font-bold"><Download size={18} /> Download PDF</a>}
          </div>
          <Divider />
          <div className="mt-4 overflow-x-auto py-4">
            <div className="flex gap-4 w-max">
              {(brochures.length ? brochures : [{ label: "Programme Overview", href: "#" }, { label: "Labs & Innovation Hub", href: "#" }, { label: "Certification Pathway", href: "#" }]).map((s, i) => (
                <motion.a key={i} href={s.href || s.url || "#"} whileHover={reduceMotion ? undefined : { scale: 1.02 }} className="min-w-[260px] md:min-w-[340px] bg-white/10 rounded-lg p-4 block text-white">
                  {s.image && <img src={safeImage(s.image)} alt={s.label} className="w-full h-40 object-cover rounded-md shadow-sm" />}
                  <h4 className="mt-3 font-semibold">{s.label || s.title}</h4>
                  <p className="text-sm mt-1 text-gray-100">{s.description || "Open this resource for more information."}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection id="gallery">
          <SectionTitle icon={Cloud}>Department Gallery</SectionTitle>
          <Divider />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(gallery.length ? gallery : []).map((g, i) => (
              <motion.img whileHover={reduceMotion ? undefined : { scale: 1.05 }} key={i} src={safeImage(g.src)} alt={g.caption || `gallery-${i}`} className="w-full h-36 object-cover rounded-lg shadow-sm" />
            ))}
            {!gallery.length && <p className="col-span-full text-gray-600">Gallery images will be updated by the department.</p>}
          </div>
        </AnimatedSection>

        <DepartmentMapSection contact={contact} mapPoints={mapPoints} reduceMotion={reduceMotion} />

        <AnimatedSection id="contact">
          <SectionTitle icon={MapPin}>Contact the Department</SectionTitle>
          <Divider />
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-700"><strong>Address:</strong> {contact.address || "Federal Polytechnic, Bida, Niger State, Nigeria"}</p>
              {contact.email && <p className="text-gray-700 flex items-center gap-2 mt-2"><Mail className="text-blue-700" /> {contact.email}</p>}
              {contact.phone && <p className="text-gray-700 flex items-center gap-2 mt-2"><Phone className="text-blue-700" /> {contact.phone}</p>}
              {contact.hours && <p className="text-gray-700 flex items-center gap-2 mt-2"><BookOpen className="text-blue-700" /> {contact.hours}</p>}
            </div>
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-sm">
              <h3 className="font-black text-blue-950">Need more information?</h3>
              <p className="mt-2 text-sm leading-6 text-blue-900">Use the contact details or visit the department office during official working hours for enquiries, admission guidance and programme information.</p>
              <a href={`mailto:${contact.email || 'info@fedpolybida.edu.ng'}`} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white shadow hover:bg-blue-600">
                <Mail size={18} /> Send Enquiry
              </a>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>
  );
}
